import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import Modal from 'react-bootstrap/Modal';
import { apiClient } from '../../components/api/ApiClient';
import JalaliDatePicker from '../../components/common/JalaliDatePicker';
import { gregorianToJalali, jalaliToGregorian, parseJalaliDate } from '../../utils/jalaliDate';
import AccountingDocument from './AccountingDocument';

function jalaliStrToGregorian(jalaliStr) {
    if (!jalaliStr) return '';
    const parsed = parseJalaliDate(jalaliStr);
    if (!parsed) return '';
    const g = jalaliToGregorian(parsed.year, parsed.month, parsed.day);
    return `${g.year}-${String(g.month).padStart(2, '0')}-${String(g.day).padStart(2, '0')}`;
}

function jalaliDateTimeStrToGregorian(jalaliStr) {
    if (!jalaliStr) return '';
    const parsed = parseJalaliDate(jalaliStr);
    if (!parsed) return '';
    const g = jalaliToGregorian(parsed.year, parsed.month, parsed.day);
    const hour = parsed.hour !== undefined ? String(parsed.hour).padStart(2, '0') : '00';
    const minute = parsed.minute !== undefined ? String(parsed.minute).padStart(2, '0') : '00';
    return `${g.year}-${String(g.month).padStart(2, '0')}-${String(g.day).padStart(2, '0')}T${hour}:${minute}:00`;
}

function gregorianDateTimeStrToJalali(gregorianStr) {
    if (!gregorianStr) return '';
    try {
        const d = new Date(gregorianStr);
        if (isNaN(d.getTime())) return '';
        const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
        const h = String(d.getHours()).padStart(2, '0');
        const m = String(d.getMinutes()).padStart(2, '0');
        return `${h}:${m} ${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`;
    } catch {
        return '';
    }
}

function gregorianStrToJalali(gregorianStr) {
    if (!gregorianStr) return '';
    try {
        const d = new Date(gregorianStr);
        if (isNaN(d.getTime())) return '';
        const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
        return `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`;
    } catch {
        return '';
    }
}

const initialItemState = {
    product_id: '',
    product_name: '',
    count: '',
    unit_price: '',
    amount: 0,
    tax_percent: 9,
    vat: 0,
    total: 0,
    measurement: '',
};

const stripLeadingZeros = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = parseFloat(String(value));
    return isNaN(num) ? 0 : num;
};

const parseNumericInput = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.max(0, Math.trunc(num));
};

const parseAmountInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const str = String(value);
    const dotIdx = str.indexOf('.');
    let intPart = dotIdx >= 0 ? str.slice(0, dotIdx) : str;
    let decPart = dotIdx >= 0 ? str.slice(dotIdx + 1) : null;
    intPart = intPart.replace(/[^0-9]/g, '').slice(0, 18) || '0';
    if (intPart !== '0') intPart = intPart.replace(/^0+/, '') || '0';
    if (decPart !== null) {
        decPart = decPart.replace(/[^0-9]/g, '').slice(0, 8);
        return `${intPart}.${decPart}`;
    }
    return intPart;
};

function InlineToast({ toasts }) {
    if (!toasts.length) return null;
    return (
        <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {toasts.map(t => (
                <div key={t.id} className={`alert mb-0 py-2 px-3 shadow ${t.type === 'success' ? 'alert-success' : 'alert-danger'}`} style={{ direction: 'rtl' }}>
                    {t.message}
                </div>
            ))}
        </div>
    );
}

function PurchaseForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const notificationId = params.get('notification_id');
    const isManualMode = !notificationId;

    const [formData, setFormData] = useState(null);
    const [sheetDateJalali, setSheetDateJalali] = useState('');
    const [invoiceDateJalali, setInvoiceDateJalali] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [showAccounting, setShowAccounting] = useState(false);
    const [savedFormId, setSavedFormId] = useState(null);

    const [availableInvoices, setAvailableInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [selectedNotificationId, setSelectedNotificationId] = useState(null);

    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newItem, setNewItem] = useState(initialItemState);

    const [availableProducts, setAvailableProducts] = useState([]);
    const [measurementUnits, setMeasurementUnits] = useState([]);

    const addToast = useCallback((message, type = 'error') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    }, []);

    useEffect(() => {
        if (!isManualMode) {
            apiClient(`/purchase/api/purchase-form/?notification_id=${notificationId}`)
                .then(data => {
                    setFormData(data);
                    setSheetDateJalali(gregorianStrToJalali(data.sheet_date));
                    setInvoiceDateJalali(gregorianDateTimeStrToJalali(data.invoice_date));
                    if (data.sheet_number) {
                        setSavedFormId(data.sheet_number);
                        setShowAccounting(true);
                        return;
                    }
                    setLoading(false);
                })
                .catch(err => {
                    addToast(err.message || 'خطا در دریافت اطلاعات فاکتور');
                    setLoading(false);
                });
        } else {
            apiClient('/purchase/api/manual-purchase-form/preview/')
                .then(data => {
                    setFormData(data);
                    setSheetDateJalali(gregorianStrToJalali(data.sheet_date));
                    setInvoiceDateJalali(gregorianDateTimeStrToJalali(data.invoice_date));
                    setLoading(false);
                })
                .catch(err => {
                    addToast(err.message || 'خطا در بارگذاری فرم');
                    setLoading(false);
                });

            apiClient('/user-preferences/api/user-product/')
                .then(data => setAvailableProducts(Array.isArray(data) ? data : (data?.data || [])))
                .catch(() => {});

            apiClient('/internal-network/api/measurement/')
                .then(data => setMeasurementUnits(Array.isArray(data) ? data : []))
                .catch(() => {});
        }
    }, [notificationId, isManualMode]);

    useEffect(() => {
        if (showAccounting) setLoading(false);
    }, [showAccounting]);

    const handleOpenInvoiceModal = () => {
        if (!isManualMode) return;
        setInvoiceSearch('');
        setShowInvoiceModal(true);
        if (availableInvoices.length === 0) {
            setInvoicesLoading(true);
            apiClient('/purchase/api/user-invoices/')
                .then(data => {
                    setAvailableInvoices(Array.isArray(data) ? data : []);
                    setInvoicesLoading(false);
                })
                .catch(err => {
                    addToast(err.message || 'خطا در دریافت فاکتورها');
                    setInvoicesLoading(false);
                });
        }
    };

    const handleSelectInvoice = (inv) => {
        setShowInvoiceModal(false);
        setInvoicesLoading(true);
        apiClient(`/purchase/api/manual-purchase-form/preview/?invoice_id=${inv.invoice_id}&notification_id=${inv.notification_id}`)
            .then(data => {
                setFormData(data);
                setSheetDateJalali(gregorianStrToJalali(data.sheet_date));
                setInvoiceDateJalali(gregorianDateTimeStrToJalali(data.invoice_date));
                setSelectedInvoiceId(inv.invoice_id);
                setSelectedNotificationId(inv.notification_id);
                setInvoicesLoading(false);
            })
            .catch(err => {
                addToast(err.message || 'خطا در بارگذاری صورتحساب');
                setInvoicesLoading(false);
            });
    };

    const handleClearInvoice = () => {
        setSelectedInvoiceId(null);
        setSelectedNotificationId(null);
        apiClient('/purchase/api/manual-purchase-form/preview/')
            .then(data => {
                setFormData(data);
                setSheetDateJalali(gregorianStrToJalali(data.sheet_date));
                setInvoiceDateJalali(gregorianDateTimeStrToJalali(data.invoice_date));
            })
            .catch(() => {});
    };

    const calculateItemCosts = (item) => {
        const count = Math.max(0, parseFloat(item.count) || 0);
        const unit_price = Math.max(0, parseFloat(item.unit_price || 0));
        const tax_percent = Math.max(0, parseFloat(item.tax_percent || 0));
        const amount = Math.trunc(count * unit_price);
        const vat = Math.trunc(amount * (tax_percent / 100));
        const total = Math.trunc(amount + vat);
        return { amount, vat, total };
    };

    const handleOpenItemModal = (item = null, index = null) => {
        if (item) {
            setNewItem({ ...initialItemState, ...item });
            setEditingItem(item);
            setEditingIndex(index);
        } else {
            setNewItem(initialItemState);
            setEditingItem(null);
            setEditingIndex(null);
        }
        setShowItemModal(true);
    };

    const handleSaveItem = () => {
        if (!newItem.product_id || !newItem.product_name || parseFloat(newItem.count) <= 0 || parseFloat(newItem.unit_price) <= 0) {
            addToast('لطفاً شناسه، عنوان، تعداد و مبلغ واحد را به درستی وارد کنید.');
            return;
        }
        const calculated = calculateItemCosts(newItem);
        const itemToSave = { ...newItem, ...calculated };
        const updatedItems = [...(formData.items || [])];
        if (editingIndex !== null) {
            updatedItems[editingIndex] = itemToSave;
        } else {
            updatedItems.push(itemToSave);
        }
        const total_amount = updatedItems.reduce((s, i) => s + (i.amount || 0), 0);
        const total_vat = updatedItems.reduce((s, i) => s + (i.vat || 0), 0);
        setFormData({ ...formData, items: updatedItems, total_amount, total_vat, total_price: total_amount + total_vat });
        setShowItemModal(false);
    };

    const handleRemoveItem = (idx) => {
        const updatedItems = (formData.items || []).filter((_, i) => i !== idx);
        const total_amount = updatedItems.reduce((s, i) => s + (i.amount || 0), 0);
        const total_vat = updatedItems.reduce((s, i) => s + (i.vat || 0), 0);
        setFormData({ ...formData, items: updatedItems, total_amount, total_vat, total_price: total_amount + total_vat });
    };

    const handleSubmit = async () => {
        if (!formData) return;
        setSubmitting(true);
        try {
            const sheetDateGregorian = jalaliStrToGregorian(sheetDateJalali);
            let saved;

            if (isManualMode) {
                const payload = {
                    invoice_id: selectedInvoiceId || null,
                    notification_id: selectedNotificationId || null,
                    invoice_number: formData.invoice_number,
                    invoice_date: jalaliDateTimeStrToGregorian(invoiceDateJalali),
                    sheet_date: sheetDateGregorian,
                    total_amount: formData.total_amount,
                    total_vat: formData.total_vat,
                    total_price: formData.total_price,
                    items: formData.items,
                    tax_id: formData.tax_id,
                };
                saved = await apiClient('/purchase/api/manual-purchase-form/', { method: 'POST', data: payload });
            } else {
                const payload = {
                    notification_id: parseInt(notificationId),
                    sheet_date: sheetDateGregorian,
                    total_amount: formData.total_amount,
                    total_vat: formData.total_vat,
                    total_price: formData.total_price,
                    items: formData.items,
                    tax_id: formData.tax_id,
                };
                saved = await apiClient('/purchase/api/purchase-form/', { method: 'POST', data: payload });
            }

            setSavedFormId(saved.sheet_number);
            setShowAccounting(true);
        } catch (err) {
            addToast(err.message || 'خطا در ثبت فرم خرید');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        setSubmitting(true);
        try {
            if (isManualMode) {
                navigate('/invoices/list/');
            } else {
                await apiClient('/purchase/api/purchase-form/cancel/', {
                    method: 'POST',
                    data: { notification_id: parseInt(notificationId) },
                });
                navigate('/notifications/');
            }
        } catch (err) {
            addToast(err.message || 'خطا در انصراف');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    if (showAccounting && savedFormId) {
        return (
            <AccountingDocument
                purchaseFormId={savedFormId}
                notificationId={isManualMode ? null : notificationId}
                onCancel={() => navigate(isManualMode ? '/invoices/list/' : '/notifications/')}
                showToast={addToast}
            />
        );
    }

    const defaults = formData?.account_defaults || {};
    const items = formData?.items || [];

    const filteredInvoices = availableInvoices.filter(inv => {
        if (!invoiceSearch) return true;
        const q = invoiceSearch.toLowerCase();
        return (
            String(inv.invoiceNum || '').includes(q) ||
            String(inv.finalDate || '').includes(q) ||
            String(inv.uniqueIdentifier || '').toLowerCase().includes(q)
        );
    });

    const productOptions = availableProducts.map(p => ({
        value: p.goods_and_services?.identity,
        label: `${p.goods_and_services?.identity} - ${p.goods_and_services?.name}`,
        product: p,
    }));

    const customSelectStyles = {
        control: (p) => ({ ...p, textAlign: 'right', direction: 'rtl' }),
        menu: (p) => ({ ...p, textAlign: 'right', direction: 'rtl', zIndex: 999999 }),
        menuPortal: (p) => ({ ...p, zIndex: 999999 }),
    };

    return (
        <div className="container-fluid" dir="rtl">
            <InlineToast toasts={toasts} />

            <div className="card shadow-sm mb-4 border-0">
                <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold text-dark">فرم نمونه خرید</h5>
                    {isManualMode && (
                        <button className="btn btn-primary btn-sm rounded-pill px-3" onClick={() => handleOpenItemModal()}>
                            <i className="feather-plus me-1" />
                            افزودن کالا/خدمت
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <div className="row g-3 mb-3">
                        <div className="col-md-2">
                            <label className="form-label fw-bold">شماره برگه</label>
                            <input type="text" className="form-control bg-light" value={formData?.sheet_number || 'خودکار'} disabled />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">تاریخ برگه</label>
                            <JalaliDatePicker value={sheetDateJalali} onChange={setSheetDateJalali} placeholder="انتخاب تاریخ شمسی" />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">شماره فاکتور</label>
                            {isManualMode ? (
                                <div
                                    className="form-control d-flex align-items-center justify-content-between"
                                    style={{ cursor: 'pointer', minHeight: 38 }}
                                    onClick={handleOpenInvoiceModal}
                                >
                                    <span className={formData?.invoice_number ? 'text-dark' : 'text-muted'}>
                                        {formData?.invoice_number || 'کلیک کنید برای انتخاب فاکتور...'}
                                    </span>
                                    <div className="d-flex align-items-center gap-2">
                                        {selectedInvoiceId && (
                                            <i
                                                className="feather-x text-danger"
                                                style={{ fontSize: 14, cursor: 'pointer' }}
                                                onClick={(e) => { e.stopPropagation(); handleClearInvoice(); }}
                                            />
                                        )}
                                        <i className="feather-search text-secondary" style={{ fontSize: 14 }} />
                                    </div>
                                </div>
                            ) : (
                                <input type="text" className="form-control bg-light" value={formData?.invoice_number || ''} disabled />
                            )}
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">تاریخ فاکتور</label>
                            <JalaliDatePicker
                                value={invoiceDateJalali}
                                onChange={isManualMode ? setInvoiceDateJalali : () => {}}
                                disabled={!isManualMode}
                                placeholder="تاریخ فاکتور"
                                showTime={true}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">شماره مالیاتی</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData?.tax_id || ''}
                                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                                placeholder="شماره مالیاتی"
                            />
                        </div>
                    </div>

                    {invoicesLoading && (
                        <div className="text-center py-2 text-muted small">
                            <span className="spinner-border spinner-border-sm me-1" />
                            در حال بارگذاری اطلاعات فاکتور...
                        </div>
                    )}

                    <div className="table-responsive mb-4 border rounded">
                        <table className="table table-bordered table-sm mb-0">
                            <thead className="table-light text-center">
                                <tr>
                                    <th>ردیف</th>
                                    <th>شناسه کالا/خدمت</th>
                                    <th>عنوان کالا/خدمت</th>
                                    {isManualMode && <th>تعداد</th>}
                                    {isManualMode && <th>نرخ واحد</th>}
                                    <th>مبلغ (ریال)</th>
                                    <th>ارزش افزوده</th>
                                    <th>مبلغ کل</th>
                                    {isManualMode && <th>عملیات</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {items.length > 0 ? (
                                    items.map((item, idx) => (
                                        <tr
                                            key={idx}
                                            className="text-center align-middle"
                                            style={isManualMode ? { cursor: 'pointer' } : {}}
                                            onClick={() => isManualMode && handleOpenItemModal(item, idx)}
                                        >
                                            <td>{idx + 1}</td>
                                            <td>{item.product_id || '-'}</td>
                                            <td className="text-start">{item.product_name || '-'}</td>
                                            {isManualMode && <td>{Number(item.count || 1).toLocaleString('fa-IR')}</td>}
                                            {isManualMode && <td>{Number(item.unit_price || 0).toLocaleString('fa-IR')}</td>}
                                            <td>{Number(item.amount || 0).toLocaleString('fa-IR')}</td>
                                            <td>{Number(item.vat || 0).toLocaleString('fa-IR')}</td>
                                            <td>{Number(item.total || 0).toLocaleString('fa-IR')}</td>
                                            {isManualMode && (
                                                <td onClick={e => e.stopPropagation()}>
                                                    <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleRemoveItem(idx)}>
                                                        <i className="feather-trash-2" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={isManualMode ? 9 : 6} className="text-center py-4 text-muted bg-light">
                                            <i className="feather-info me-2 text-warning" />
                                            {isManualMode ? 'هیچ کالا یا خدمتی افزوده نشده است.' : 'آیتمی یافت نشد.'}
                                        </td>
                                    </tr>
                                )}
                                <tr className="table-secondary fw-bold text-center">
                                    <td colSpan={isManualMode ? 5 : 3}>جمع کل</td>
                                    <td>{Number(formData?.total_amount || 0).toLocaleString('fa-IR')}</td>
                                    <td>{Number(formData?.total_vat || 0).toLocaleString('fa-IR')}</td>
                                    <td>{Number(formData?.total_price || 0).toLocaleString('fa-IR')}</td>
                                    {isManualMode && <td />}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="border rounded p-2 bg-light">
                                <div className="mb-1"><span className="fw-bold">کد حساب خرید: </span><span>{defaults.purchase_account_code}</span></div>
                                <div><span className="fw-bold">عنوان حساب: </span><span>{defaults.purchase_account_name}</span></div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="border rounded p-2 bg-light">
                                <div className="mb-1"><span className="fw-bold">کد حساب ارزش افزوده: </span><span>{defaults.vat_account_code}</span></div>
                                <div><span className="fw-bold">عنوان حساب ارزش افزوده: </span><span>{defaults.vat_account_name}</span></div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="border rounded p-2 bg-light">
                                <div className="mb-1"><span className="fw-bold">کد حساب طرف مقابل: </span><span>{defaults.counterpart_account_code}</span></div>
                                <div><span className="fw-bold">عنوان حساب: </span><span>{defaults.counterpart_account_name}</span></div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex gap-2 justify-content-end">
                        <button className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>انصراف</button>
                        <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                            {submitting && <span className="spinner-border spinner-border-sm me-1" />}
                            ثبت سند
                        </button>
                    </div>
                </div>
            </div>

            <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} centered size="lg">
                <Modal.Header closeButton style={{ direction: 'rtl' }}>
                    <Modal.Title className="fw-bold">انتخاب صورتحساب تأیید شده</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ direction: 'rtl' }}>
                    <div className="mb-3">
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="feather-search text-muted" />
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0"
                                placeholder="جستجو بر اساس شماره فاکتور، تاریخ، شناسه مالیاتی..."
                                value={invoiceSearch}
                                onChange={e => setInvoiceSearch(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <table className="table table-hover table-bordered table-sm mb-0">
                            <thead className="table-light text-center" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th>شماره فاکتور</th>
                                    <th>تاریخ</th>
                                    <th>مبلغ کل</th>
                                    <th>شناسه مالیاتی</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoicesLoading ? (
                                    <tr><td colSpan={4} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" />در حال بارگذاری...</td></tr>
                                ) : filteredInvoices.length > 0 ? (
                                    filteredInvoices.map(inv => (
                                        <tr
                                            key={inv.invoice_id}
                                            style={{ cursor: 'pointer' }}
                                            className={`text-center align-middle ${selectedInvoiceId === inv.invoice_id ? 'table-primary fw-bold' : ''}`}
                                            onClick={() => handleSelectInvoice(inv)}
                                        >
                                            <td>{inv.invoiceNum || '-'}</td>
                                            <td>{inv.finalDate || '-'}</td>
                                            <td>{Number(inv.sumTotalPrice || 0).toLocaleString('fa-IR')}</td>
                                            <td className="text-muted small">{inv.uniqueIdentifier || '-'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={4} className="text-center py-4 text-muted">صورتحسابی یافت نشد</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="text-muted small mt-2">
                        <i className="feather-info me-1" />
                        فقط فاکتورهایی که تأیید شده‌اند ولی هنوز سند خرید برایشان ثبت نشده نمایش داده می‌شوند.
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showItemModal} onHide={() => setShowItemModal(false)} centered size="lg">
                <Modal.Header closeButton style={{ direction: 'rtl' }}>
                    <Modal.Title className="fw-bold">{editingItem ? 'ویرایش کالا/خدمت' : 'افزودن کالا/خدمت'}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ direction: 'rtl' }}>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">شناسه کالا/خدمت <span className="text-danger">*</span></label>
                            <CreatableSelect
                                isClearable
                                isSearchable
                                placeholder="شناسه کالا را وارد یا انتخاب کنید..."
                                options={productOptions}
                                value={newItem.product_id ? (productOptions.find(o => o.value === newItem.product_id) || { value: newItem.product_id, label: newItem.product_id }) : null}
                                onChange={(opt) => {
                                    if (!opt) { setNewItem(p => ({ ...p, product_id: '' })); return; }
                                    if (opt.product) {
                                        const sp = opt.product;
                                        setNewItem(p => ({
                                            ...p,
                                            product_id: sp.goods_and_services?.identity || '',
                                            product_name: sp.goods_and_services?.name || p.product_name,
                                            tax_percent: parseFloat(sp.goods_and_services?.taxPercent ?? 9),
                                        }));
                                    } else {
                                        setNewItem(p => ({ ...p, product_id: opt.value }));
                                    }
                                }}
                                formatCreateLabel={(v) => `استفاده از "${v}" به عنوان شناسه`}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-bold">عنوان کالا/خدمت <span className="text-danger">*</span></label>
                            <input type="text" className="form-control" value={newItem.product_name || ''} onChange={e => setNewItem({ ...newItem, product_name: e.target.value })} placeholder="عنوان کالا یا خدمت..." />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">واحد اندازه‌گیری</label>
                            <Select
                                options={measurementUnits.map(u => ({ value: u.code, label: u.name }))}
                                value={measurementUnits.find(u => u.code === newItem.measurement) ? { value: newItem.measurement, label: measurementUnits.find(u => u.code === newItem.measurement).name } : null}
                                onChange={opt => setNewItem({ ...newItem, measurement: opt ? opt.value : '' })}
                                placeholder="انتخاب کنید..."
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">تعداد/مقدار <span className="text-danger">*</span></label>
                            <input type="text" inputMode="decimal" className="form-control" value={newItem.count === 0 || newItem.count === '' ? '' : newItem.count} onChange={e => setNewItem({ ...newItem, count: parseAmountInput(e.target.value) })} placeholder="0" />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">مبلغ واحد (ریال) <span className="text-danger">*</span></label>
                            <input type="number" onWheel={e => e.target.blur()} min="0" className="form-control" value={stripLeadingZeros(newItem.unit_price)} onChange={e => setNewItem({ ...newItem, unit_price: parseNumericInput(e.target.value) })} placeholder="0" />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">مبلغ قبل از مالیات</label>
                            <input type="text" className="form-control bg-light" value={calculateItemCosts(newItem).amount.toLocaleString('fa-IR')} disabled />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">نرخ مالیات (%)</label>
                            <input type="number" onWheel={e => e.target.blur()} min="0" max="100" className="form-control" value={stripLeadingZeros(newItem.tax_percent)} onChange={e => setNewItem({ ...newItem, tax_percent: Math.min(100, parseNumericInput(e.target.value)) })} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">مبلغ مالیات</label>
                            <input type="text" className="form-control bg-light" value={calculateItemCosts(newItem).vat.toLocaleString('fa-IR')} disabled />
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-bold text-dark">مبلغ کل (ریال)</label>
                            <input type="text" className="form-control fw-bold text-primary bg-light fs-5 py-2" value={calculateItemCosts(newItem).total.toLocaleString('fa-IR')} disabled />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ direction: 'rtl' }}>
                    <button className="btn btn-secondary" onClick={() => setShowItemModal(false)}>انصراف</button>
                    <button className="btn btn-primary" onClick={handleSaveItem}>{editingItem ? 'ذخیره تغییرات' : 'افزودن'}</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default PurchaseForm;
